import React, { useContext, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { IonContent } from '@ionic/react';
import img from '../../../assets/foodfight_under_review.png';
import { AppContext } from '../../../global/AppContext';
import { createPayment, createTestPayment } from '../../../global/request/bet';
import { AuthContext } from '../../authentication/AuthContext';
import ModalHeader from '../ModalHeader';
import { StartFightContext, ACTIONS } from '../StartFightContext';
import ReferFriendModalSummary from '../challenger/ReferFriendModalSummary';
import TakerError from '../takerFightProcess/TakerError';
import useCanGo from '../useCanGo';
import StripeForm from './StripeForm';
import '../../../styles/fight/summary/PaymentStep.scss';

const PaymentStep = ({ pageIndex }) => {
  const appCtx = useContext(AppContext);
  const startFightCtx = useContext(StartFightContext);
  const [clientSecret, setClientSecret] = useState('');
  const authCtx = useContext(AuthContext);
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const contentRef = useRef();
  const [showReferFriendModal, setShowReferFriendModal] = useState(false);

  const stripePromise = useMemo(
    () =>
      window.location.hostname === 'www.getfoodfight.link'
        ? loadStripe(process.env.REACT_APP_STRIPE_LIVE_KEY)
        : loadStripe(process.env.REACT_APP_STRIPE_TEST_KEY),
    [],
  );

  const sendPayment = useCallback(async () => {
    let totalAmount = 0.0;
    startFightCtx.items.forEach(item => {
      totalAmount += item.price * item.amount;
    });
    if (totalAmount > 0) {
      const payment =
        window.location.hostname === 'www.getfoodfight.link'
          ? await createPayment(authCtx.tokens.idToken, totalAmount, appCtx.isDelivery)
          : await createTestPayment(authCtx.tokens.idToken, totalAmount, appCtx.isDelivery);
      setClientSecret(payment);
    }
  }, [appCtx.isDelivery, authCtx.tokens.idToken, startFightCtx.items]);

  useEffect(() => {
    sendPayment();
  }, [authCtx.tokens.idToken, sendPayment]);

  const handleErrorModalClose = () => {
    setIsErrorOpen(false);
    startFightCtx.dispatch({
      action: ACTIONS.SET_ERROR_MESSAGE,
      payload: '',
    });
  };

  useEffect(() => {
    setIsErrorOpen(Boolean(startFightCtx.errorMessage));
  }, [startFightCtx.errorMessage]);

  useEffect(() => {
    if (startFightCtx.isPaymentRequired && startFightCtx.isReferClicked && startFightCtx.foodfightId) {
      setShowReferFriendModal(true);
      startFightCtx.dispatch({
        action: ACTIONS.SET_REFERRAL_MODAL_OPEN,
        payload: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startFightCtx.foodfightId, startFightCtx.isReferClicked]);

  useCanGo(pageIndex, () => startFightCtx.isPaymentRequired && startFightCtx.items.length > 0, [
    startFightCtx.isPaymentRequired,
    startFightCtx.items,
  ]);

  if (!startFightCtx.isPaymentRequired || !clientSecret) return null;

  const appearance = {
    theme: 'stripe',
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <>
      <ModalHeader pageName='Create Payment' />
      <IonContent ref={contentRef} id='payment-step-modal-page' className='ion-padding'>
        <div id='payment'>
          {startFightCtx.isPaymentRequired && clientSecret && (
            <Elements options={options} stripe={stripePromise}>
              <StripeForm clientSecret={clientSecret} formSubmitRef={startFightCtx.formRef} />
            </Elements>
          )}
          <p className='disclaimer-text'>
            *By proceeding you authorize a 48 hr hold on your card for the amount listed in the Fight Cart. Win =
            authorization canceled; Lose = authorization processed.
          </p>
        </div>
      </IonContent>
      <TakerError
        isOpen={isErrorOpen}
        onClose={handleErrorModalClose}
        setIsOpen={setIsErrorOpen}
        msg={startFightCtx.errorMessage || 'Error finalizing details.'}
        isListenerRequired
      />
      {startFightCtx.submitted && (
        <div id='fight-under-review-payment'>
          <img src={img} alt='FoodFight under review' />
        </div>
      )}
      {showReferFriendModal && startFightCtx.foodfightId && (
        <ReferFriendModalSummary
          isOpen={showReferFriendModal}
          onClose={() => setShowReferFriendModal(false)}
          bearerToken={authCtx.tokens.idToken}
          foodfightId={startFightCtx.foodfightId} // optional: if you've stored it
        />
      )}
    </>
  );
};

export default PaymentStep;
